<template>
  <div class="list condition-list">
    <div class="condition-list__lines">
      <template v-for="(condition, index) of conditions" :key="condition.id">
        <div v-if="index > 0" class="condition-list__connector">
          {{ $t("words.and") }}
        </div>
        <button-item
          :selected="selectedCondition?.id === condition.id"
          class="condition-list__condition"
          @click.prevent="onSelect(condition)"
          @contextmenu.prevent="openContextMenu(condition, $event)"
        >
          <condition-label :condition-id="condition.id" />
          <template #icons>
            <button
              type="button"
              class="button-icon button-item__icon"
              @click.prevent="onDelete(condition)"
            >
              <lucide-icon :name="'trash-2'" />
            </button>
          </template>
        </button-item>
      </template>
    </div>

    <button
      type="button"
      class="button button_secondary mt-1"
      @click.prevent="create()"
    >
      {{ $t("actions.addCondition") }}
    </button>

    <context-menu
      :visible="contextMenuState.condition != null"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      @close="contextMenuState.condition = null"
    >
      <button
        v-if="contextMenuState.condition"
        type="button"
        class="context-menu__entry"
        @click="$emit('copy', contextMenuState.condition)"
      >
        {{ $t("actions.copyCondition") }}
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import ConditionLabel from "./ConditionLabel.vue";

import { useConditionStore } from "@editor/items/condition/conditions.store";

import type { ConditionItem } from "./ConditionItem";

interface Props {
  deletable?: boolean;
  srcType?: ConditionItem["srcType"];
  srcId?: ConditionItem["srcId"];
  availableActivatorTypes?: ConditionItem["activatorType"][];
  selectedCondition?: { type: "condition"; id: number };
  excludedConditions?: number[];
}
const props = withDefaults(defineProps<Props>(), {
  deletable: true,
  srcType: undefined,
  srcId: undefined,
  availableActivatorTypes: undefined,
  selectedCondition: undefined,
  excludedConditions: () => []
});

const emit = defineEmits<{
  (e: "created", condition: ConditionItem): void;
  (e: "select", condition: ConditionItem): void;
  (e: "deleted", condition: ConditionItem): void;
  (e: "copy", condition: ConditionItem): void;
}>();

const conditionStore = useConditionStore();

const conditions = computed(() => {
  return conditionStore.orderedItems.filter(
    (condition) =>
      props.excludedConditions.indexOf(condition.id) === -1 &&
      condition.srcType === props.srcType &&
      condition.srcId === props.srcId
  );
});

async function create(type?: ConditionItem["activatorType"]) {
  const condition = await conditionStore.create({
    srcType: props.srcType,
    srcId: props.srcId,
    activatorType: type
  });
  emit("created", condition);
  emit("select", condition);
}

function onSelect(condition: ConditionItem) {
  emit("select", condition);
}

async function onDelete(condition: ConditionItem) {
  await conditionStore.delete(condition);
  emit("deleted", condition);
}

const contextMenuState = reactive({
  condition: null as ConditionItem | null,
  x: 0,
  y: 0
});
function openContextMenu(condition: ConditionItem, event: MouseEvent) {
  contextMenuState.condition = condition;
  contextMenuState.x = event.clientX;
  contextMenuState.y = event.clientY;
}
</script>

<style lang="scss">
.condition-list {
  &__lines {
    display: grid;
    grid-template-columns: min-content 1fr;
    gap: 0.5rem;
  }
  &__condition {
    &:first-child {
      grid-column-start: 2;
      grid-column-end: 3;
    }
    &:first-child:last-child {
      grid-column-start: 1;
      grid-column-end: 3;
    }
  }
  &__connector {
    color: var(--color-text-medium);
    font-size: 11px;
    position: relative;
    width: max-content;
    align-self: center;
  }
}
</style>
